import React, { useState, useEffect } from "react";
import { Layout } from "./components/Layout.tsx";
import { Dashboard } from "./components/Dashboard.tsx";
import { SOWUpload } from "./components/SOWUpload.tsx";
import { TranscriptList } from "./components/TranscriptList.tsx";
import { TaskGroups } from "./components/TaskGroups.tsx";
import { DueDateMover } from "./components/DueDateMover.tsx";
import { Notes } from "./components/Notes.tsx";
import { ConfigPanel } from "./components/ConfigPanel.tsx";
import { CallNotes } from "./components/CallNotes.tsx";
import { ScopingEmails } from "./components/ScopingEmails.tsx";
import { QAAutomation } from "./components/QAAutomation.tsx";
import { LoginPage } from "./components/LoginPage.tsx";

export function App() {
  const [authState, setAuthState] = useState<"loading" | "authenticated" | "unauthenticated">("loading");
  const [authError, setAuthError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const [page, setPage] = useState(() => {
    const hash = window.location.hash.slice(1);
    return hash || "dashboard";
  });

  useEffect(() => {
    // Check for auth errors from magic link redirect
    const params = new URLSearchParams(window.location.hash.split("?")[1] || "");
    if (params.get("error")) {
      setAuthError(
        params.get("error") === "invalid_or_expired"
          ? "Login link is invalid or expired. Please try again."
          : "Login failed. Please try again."
      );
    }

    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((data: { authenticated: boolean; email?: string }) => {
        if (data.authenticated) {
          setAuthState("authenticated");
          setEmail(data.email || null);
        } else {
          setAuthState("unauthenticated");
        }
      })
      .catch(() => setAuthState("unauthenticated"));
  }, []);

  useEffect(() => {
    const onHash = () => setPage(window.location.hash.slice(1) || "dashboard");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setAuthState("unauthenticated");
    setEmail(null);
  };

  const navigate = (p: string) => {
    window.location.hash = p;
    setPage(p);
  };

  if (authState === "loading") {
    return <div className="loading" style={{ minHeight: "100vh", alignItems: "center" }}>Loading...</div>;
  }

  if (authState === "unauthenticated") {
    return <LoginPage error={authError} />;
  }

  let content: React.ReactNode;
  switch (page) {
    case "dashboard":
      content = <Dashboard />;
      break;
    case "projects":
      // Redirect legacy hash to settings
      navigate("settings");
      break;
    case "sow-upload":
      content = <SOWUpload />;
      break;
    case "notes":
      content = <Notes />;
      break;
    case "transcripts":
      content = <TranscriptList />;
      break;
    case "task-groups":
      content = <TaskGroups />;
      break;
    case "due-date-mover":
      content = <DueDateMover />;
      break;
    case "call-notes":
      content = <CallNotes />;
      break;
    case "scoping-emails":
      content = <ScopingEmails />;
      break;
    case "qa":
      content = <QAAutomation />;
      break;
    case "settings":
      content = <ConfigPanel />;
      break;
    default:
      content = <Dashboard />;
  }

  return (
    <Layout currentPage={page} onNavigate={navigate} email={email} onLogout={handleLogout}>
      {content}
    </Layout>
  );
}
